import { useRef, useState } from 'react';
import { ArdoqId, PersonalSetting, ViewIds } from '@ardoq/api-types';
import { ComponentDataSourceItem, ComponentTreeViewSettings } from '../types';
import { ComponentTree } from './ComponentTree';
import { getViewModel$ } from '../models/viewModel$';
import { KnowledgeBaseLink } from '@ardoq/knowledge-base';
import { isPresentationMode } from 'env';
import { SettingsBar } from '@ardoq/settings-bar';
import { getRightMenuConfig } from 'viewSettings/getRightMenuConfig';
import { getExportSettings } from 'viewSettings/getExportSettings';
import { onViewSettingsUpdate } from 'tabview/onViewSettingsUpdate';
import { getLeftMenu } from '../utils/menuConfigParams';
import {
  ComponentTreeContainer,
  ContextMenuWrapper,
  ViewContainer,
} from '../atoms';
import {
  ViewError,
  getComponentTypesInfo,
  ViewLegendContainer,
} from '@ardoq/graph';
import { getAdditionalRowsForGraphViewLegend } from 'tabview/graphViews/graphViewLegend/graphViewLegendUtils';
import { useViewLegendSubscription } from 'views/viewLegend/useViewLegendSubscription';
import { ErrorInfoBox } from '@ardoq/error-info-box';
import type { ErrorInfoBoxLabels } from 'locales/getErrorInfoBoxLabels';
import { noop } from 'lodash';
import { usePersonalSettingToggle } from 'streams/currentUser/usePersonalSettingToggle';
import { OBJECT_CONTEXT_MENU_NAME } from '@ardoq/context-menu';
import { componentTreeCommands } from '../commands';
import {
  getActiveConditionalFormattingForLegend,
  ViewLegend,
} from '@ardoq/view-legend';
import { getViewLegendLabels } from 'viewLegend/getViewLegendLabels';
import type { I18nLocale } from '@ardoq/i18n';
import { connectInstanceWithProps } from 'streams/utils/connectInstanceWithProps';

type ComponentTreeViewProps = {
  viewSettings: ComponentTreeViewSettings;
  sourceDataWithRenderingData: ComponentDataSourceItem[];
  collapsed: Record<ArdoqId, boolean>;
  collapsibleIds: ArdoqId[];
  focusedComponentId: ArdoqId | null;
  hoveredComponentId: ArdoqId | null;
  errors: ViewError[];
  errorInfoBoxLabels: ErrorInfoBoxLabels;
  i18nLocale: I18nLocale;
};

const VIEW_ID = ViewIds.COMPONENTTREE;

export const ComponentTreeView = (props: ComponentTreeViewProps) => {
  const {
    viewSettings,
    sourceDataWithRenderingData,
    collapsed,
    collapsibleIds,
    errors,
    errorInfoBoxLabels,
    i18nLocale,
  } = props;
  const { isLegendActive } = viewSettings;

  const containerRef = useRef<HTMLDivElement | null>(null);

  const [clearedErrors, setClearedErrors] = useState(false);

  const [neverShowAgain, toggleNeverShowAgain] = usePersonalSettingToggle(
    PersonalSetting.NEVER_SHOW_CLONE_INFO_AGAIN_COMPONENT_TREE
  );

  const activeConditionalFormattingForLegend =
    getActiveConditionalFormattingForLegend(i18nLocale);

  const additionalRows = getAdditionalRowsForGraphViewLegend({
    componentTypes: getComponentTypesInfo(
      sourceDataWithRenderingData.map(({ id }) => id)
    ).legendComponentTypes,
    referenceTypes: [],
    hasCollapsedNodes: false,
    hasReferenceParents: false,
    hasNonComponentNodes: false,
    isUserDefinedGrouping: false,
    showComponentSwatches: true,
    activeConditionalFormattingForLegend: {
      components: activeConditionalFormattingForLegend.components,
      tags: activeConditionalFormattingForLegend.tags,
      references: [],
    },
    showComponentShapes: false,
    showReferencesAsLines: false,
    selectableItems: false,
    isNewConditionalFormatting: false,
    i18nLocale,
  });

  return (
    <ContextMenuWrapper data-context-menu={OBJECT_CONTEXT_MENU_NAME}>
      {!isPresentationMode() && (
        <SettingsBar
          viewId={VIEW_ID}
          leftMenu={getLeftMenu({ collapsed, collapsibleIds })}
          rightMenu={getRightMenuConfig({
            isViewpointMode: false,
            viewId: VIEW_ID,
            withLegend: true,
            viewstate: viewSettings,
            exports: getExportSettings(VIEW_ID, containerRef, i18nLocale),
            knowledgeBaseLink: KnowledgeBaseLink.COMPONENT_TREE,
            onViewSettingsUpdate,
            i18nLocale,
          })}
          i18nLocale={i18nLocale}
        />
      )}

      <ViewContainer
        ref={containerRef}
        onClick={() => {
          componentTreeCommands.unfocusFocusedComponent();
        }}
      >
        <ComponentTreeContainer>
          <ComponentTree
            dataSource={sourceDataWithRenderingData}
            i18nLocale={i18nLocale}
          />
        </ComponentTreeContainer>

        <ErrorInfoBox
          errors={clearedErrors ? [] : errors}
          hasClones={false}
          clearHasClones={noop}
          isShowNeverAgainSet={neverShowAgain}
          isPresentationMode={isPresentationMode()}
          clearErrors={() => setClearedErrors(true)}
          toggleNeverShowAgain={toggleNeverShowAgain}
          {...errorInfoBoxLabels}
        />

        <ViewLegendContainer
          heightOffset={useViewLegendSubscription()}
          visible={isLegendActive}
          // setting this here because margin-left style set by className is ignored while exporting to PNG
          style={{ marginLeft: 'auto' }}
        >
          <ViewLegend
            componentTypes={[]}
            referenceTypes={[]}
            additionalRows={additionalRows}
            activeDiffMode={null}
            labels={getViewLegendLabels(i18nLocale)}
          />
        </ViewLegendContainer>
      </ViewContainer>
    </ContextMenuWrapper>
  );
};

const ConnectedComponentTree = connectInstanceWithProps(
  ComponentTreeView,
  getViewModel$
);

export { ConnectedComponentTree };
